import { formatDate } from "@angular/common";
import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { StateService } from "@uirouter/core";
import { ChartTooltipItem } from "chart.js";
import { cloneDeep, merge } from "lodash";
import { ChartHelperService } from "../chart/chart-helper.service";
import { CSSColorService } from "../css-color/css-color.service";
import { EnvColorService } from "../env-color/env-color.service";
import { formatNumberToHuman } from "../number-to-human/number-to-human";
import { pluralize } from "../utils/pluralize";

@Component({
  selector: "ab-deploy-barchart",
  templateUrl: "./deploy-barchart.component.html",
})
export class DeployBarchartComponent implements OnChanges {
  @Input() public data: any;
  @Input() public environment: string;
  @Input() public loading: boolean;
  @Input() public mode: string;

  public chartData: any;

  private DATE_FORMAT = "MMM dd, y HH:mm:ss Z";
  private DEPLOY_MODE = "deploy";
  private GROUP_MODE = "group";
  private INTRODUCED_GROUP_COLOR =
    this.cssColorService.getColor("--ab-red-400");
  private RESOLVED_GROUP_COLOR = this.cssColorService.getColor("--ab-blue-400");

  constructor(
    private $state: StateService,
    private chartHelperService: ChartHelperService,
    private cssColorService: CSSColorService,
    private envColorService: EnvColorService,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.data == null) {
      return;
    }
    if (changes.data.isFirstChange()) {
      return;
    }

    this.chartData = this.calcData(cloneDeep(this.data));
  }

  public calcData(data: any[]) {
    data = this.sortDataByDate(data);
    const deploysForSelectedEnv: any[] = data.filter((d) => {
      return d.environment === this.environment || this.environment === "all";
    });
    if (this.mode === this.DEPLOY_MODE) {
      return this.deploysChartData(deploysForSelectedEnv);
    }
    if (this.mode === this.GROUP_MODE) {
      return this.groupsChartData(deploysForSelectedEnv);
    } else {
      throw new Error(`invalid deploy chart mode: ${this.mode}`);
    }
  }

  public deploysChartData(deploys: any[]) {
    const comp = this;
    const deployColors = deploys.map((deploy: any) => {
      return this.envColorService.colorString(deploy.environment);
    });
    return {
      data: {
        labels: deploys.map((_d: any, index: number) => index),
        datasets: [
          {
            label: "Occurrence Rate (errors/min)",
            deploys,
            data: deploys.map((deploy) => deploy.occurrenceRate),
            backgroundColor: deployColors,
            borderColor: deployColors,
          },
        ],
      },
      options: merge({}, this.chartHelperService.defaultChartOptions(), {
        legend: {
          display: true,
          onClick: null,
        },
        tooltips: {
          intersect: true,
          callbacks: {
            title: (tooltipItems: ChartTooltipItem[], data: any) => {
              if (tooltipItems.length === 0) {
                return;
              }

              const dataset = data.datasets[tooltipItems[0].datasetIndex];
              const deploy = dataset.deploys[tooltipItems[0].index];
              return formatDate(deploy.createdAt, this.DATE_FORMAT, "en-US");
            },
            label: (tooltipItem: ChartTooltipItem, data: any) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const deploy = dataset.deploys[tooltipItem.index];

              let formattedOccurrenceRate;
              if (deploy.occurrenceRate > 0 && deploy.occurrenceRate < 0.1) {
                formattedOccurrenceRate = "~0/min";
              } else {
                formattedOccurrenceRate =
                  `${formatNumberToHuman(deploy.occurrenceRate.toFixed(1))}` +
                  ` ${pluralize(deploy.occurrenceRate, "occurrence")}/min`;
              }

              return (
                formattedOccurrenceRate +
                ` (${formatNumberToHuman(deploy.noticeTotalCount)}` +
                ` ${pluralize(deploy.noticeTotalCount, "occurrence")})`
              );
            },
          },
        },
        onClick: comp.onClick,
        onHover: comp.onHover,
        scales: {
          xAxes: [
            {
              gridLines: {
                drawTicks: false,
              },
              ticks: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                callback: this.tickFormat,
              },
            },
          ],
        },
      }),
    };
  }

  public groupsChartData(deploys: any[]) {
    const comp = this;
    return {
      data: {
        labels: deploys.map((_d: any, index: number) => index),
        datasets: [
          {
            label: "Errors Fixed",
            data: deploys.map((deploy: any) => deploy.groupResolvedCount),
            backgroundColor: this.RESOLVED_GROUP_COLOR,
            borderColor: this.RESOLVED_GROUP_COLOR,
            deploys,
          },
          {
            label: "Errors Introduced",
            data: deploys.map((deploy: any) => -deploy.groupCreatedCount),
            backgroundColor: this.INTRODUCED_GROUP_COLOR,
            borderColor: this.INTRODUCED_GROUP_COLOR,
            deploys,
          },
        ],
      },
      options: merge({}, this.chartHelperService.defaultChartOptions(), {
        legend: {
          display: true,
          onClick: null,
        },
        tooltips: {
          intersect: true,
          callbacks: {
            title: (tooltipItems: ChartTooltipItem[], data: any) => {
              if (tooltipItems.length === 0) {
                return;
              }
              const dataset = data.datasets[tooltipItems[0].datasetIndex];
              const deploy = dataset.deploys[tooltipItems[0].index];
              return formatDate(deploy.createdAt, this.DATE_FORMAT, "en-US");
            },
            label: (tooltipItem: ChartTooltipItem, data: any) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const deploy = dataset.deploys[tooltipItem.index];
              if (tooltipItem.datasetIndex === 0) {
                return (
                  `Fixed ${deploy.groupResolvedCount}` +
                  ` ${pluralize(deploy.groupResolvedCount, "error")}`
                );
              }
              if (tooltipItem.datasetIndex === 1) {
                return (
                  `Introduced ${deploy.groupCreatedCount}` +
                  ` ${pluralize(deploy.groupCreatedCount, "error")}`
                );
              }
            },
          },
        },
        onClick: comp.onClick,
        onHover: comp.onHover,
        scales: {
          xAxes: [
            {
              gridLines: {
                drawTicks: false,
              },
              ticks: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                callback: this.tickFormat,
                min: Math.min(...deploys.map((d: any) => -d.groupCreatedCount)),
              },
            },
          ],
        },
      }),
    };
  }

  private onClick = (event: MouseEvent, activeElements: any[]) => {
    if (!activeElements[0]) {
      return;
    }

    const chart = activeElements[0]._chart;
    const elements = chart.getElementsAtEvent(event);

    if (!elements.length) {
      return;
    }

    const barIndex = elements[0]._index;
    const datasetIndex = elements[0]._datasetIndex;
    const dataset = chart.config.data.datasets[datasetIndex];
    const deploy = dataset.deploys[barIndex];

    this.chartHelperService.removeTooltip();
    this.$state.go("deploys-show", {
      projectId: deploy.projectId,
      deployId: deploy.id,
    });
  };

  private onHover = (event: MouseEvent, activeElements: any[]) => {
    const canvas = event.target as HTMLCanvasElement;

    if (!activeElements[0]) {
      canvas.style.cursor = "default";
      return;
    }

    const chart = activeElements[0]._chart as Chart;
    const elements = chart.getElementsAtEvent(event);

    canvas.style.cursor = elements.length ? "pointer" : "default";
  };

  private tickFormat = (value: any, _index: any, _values: any) => {
    return formatNumberToHuman(Math.abs(value));
  };

  private sortDataByDate(data: any[]) {
    // Sort by descending createdAt dates
    return data.sort((a: any, b: any) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return aTime - bTime;
    });
  }
}
